import React, { useEffect } from "react";
import {
  APIProvider,
  Map as MapBase,
  MapProps as MapBaseProps,
  AdvancedMarker,
  Pin,
  useMap,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import colors from "@secondcloset/web-components/dist/styles/colors";

const API_KEY = process.env.REACT_APP_CORE_API;

export interface MapProps extends MapBaseProps {
  markers: { lat: number; lng: number; address: string }[];
}

const getCenterPoint = (markers: MapProps["markers"]) => {
  const total = markers.reduce(
    (acc, cur, i) => ({
      ...acc,
      lat: acc.lat + cur.lat,
      lng: acc.lng + cur.lng,
    }),
    { lat: 0, lng: 0 }
  );
  return { lat: total.lat / markers.length, lng: total.lng / markers.length };
};

const Mapbox: React.FC<MapProps> = ({ markers, ...props }) => {
  const center = getCenterPoint(markers);
  const map = useMap();
  const gMapsLib = useMapsLibrary("maps");
  const gCoreLib = useMapsLibrary("core");

  useEffect(() => {
    if (!map || !gMapsLib || !gCoreLib) return;

    const line = new gMapsLib.Polyline({
      path: markers,
      geodesic: true,
      strokeColor: colors.PRIMARY,
      strokeOpacity: 1.0,
      strokeWeight: 4,
    });

    line.setMap(map);

    const bounds = new gCoreLib.LatLngBounds();
    bounds.extend(markers[0]);
    bounds.extend(markers[1]);
    map.fitBounds(bounds);

    return () => {
      line.unbindAll();
      map.unbindAll();
    };
  }, [map, gMapsLib, gCoreLib, markers]);

  return (
    <MapBase defaultCenter={center} defaultZoom={4} gestureHandling={"greedy"} disableDefaultUI={true} {...props}>
      {markers.map((location, i) => (
        <AdvancedMarker key={i} position={location}>
          <Pin background={colors.PRIMARY} glyphColor={colors.CHARCOAL} borderColor={colors.CHARCOAL} scale={0.8} />
        </AdvancedMarker>
      ))}
    </MapBase>
  );
};

export const Map: React.FC<MapProps> = (props) => {
  return (
    <APIProvider apiKey={API_KEY || ""}>
      <Mapbox {...props} />
    </APIProvider>
  );
};
